import { exists } from '@aboutyou/legacy-shared/src/utils/exists'
import {
  RecoSectionContent,
  TrackingPageTypeWithId,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk'
import PromotionLink from 'components/PromotionLink'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { getImageUrl } from 'helpers/image'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { RecoGridSectionTrackingContextProvider } from 'helpers/tracking/SectionTrackingContext'
import { PromotionImpression } from 'helpers/tracking/trackerComponents/ElementTracker'
import React, { FC } from 'react'
import { useService } from 'services/utils/context'
import { usePremiumPageStore } from 'store/context'
import Outfit from 'store/entities/Outfit/Outfit.store'
import styled, { css } from 'styled-components'
import ImageBox from 'uiComponents/atoms/ImageBox/ImageBox'
import { primaryColor } from 'uiComponents/constants/colors'
import PremiumLandingSection from './PremiumLandingSection'
import { SectionHeaderDefault } from './PremiumSectionHeaders'

const Header = styled(SectionHeaderDefault)`
  margin-top: 100px;
`

const OutfitTile = styled.span`
  display: block;
  margin-bottom: 22px;
  position: relative;
`

const fullAbsolute = css`
  position: absolute;
  width: 100%;
  height: 100%;
`
const Gradient = styled.div`
  ${fullAbsolute};
  background-image: linear-gradient(#000, rgba(0, 0, 0, 0.3) 0%);
  top: 0;
`
const TitleWrapper = styled.span`
  display: inline-block;
  position: relative;
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    height: 1px;
    width: 100%;
    background-color: ${primaryColor};
  }
`

const PremiumGapGrid = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
`

const PremiumGapColumn = styled.div`
  text-align: center;
  flex-basis: calc(33.333% - 15px);
`

const OUTFIT_POSITIONS_START = 3
const OUTFIT_POSITIONS_END = 6

const PremiumLooks: FC = () => {
  const service = useService()
  const { t } = useTranslation()
  const outfitIds = usePremiumPageStore(store => store.premiumOutfitIds)

  const outfits = outfitIds
    .slice(OUTFIT_POSITIONS_START, OUTFIT_POSITIONS_END)
    .map(id => {
      const outfit = service.outfits.getById(id)
      return outfit
    })
    .filter(exists)

  if (outfits.length === 0) {
    return null
  }

  const title = t('Premium Looks')

  return (
    <RecoGridSectionTrackingContextProvider
      headline={title}
      recoType={RecoSectionContent.outfitRecommendationCategory}
    >
      <PremiumLandingSection sectionTitle={<Header title={title} />}>
        <PremiumGapGrid data-testid="PremiumLooks">
          {outfits.map(outfit => {
            return <TrackedOutfitTile key={outfit.id} outfit={outfit} />
          })}
        </PremiumGapGrid>
      </PremiumLandingSection>
    </RecoGridSectionTrackingContextProvider>
  )
}

export default PremiumLooks

const TrackedOutfitTile: FC<{ outfit: Outfit }> = ({ outfit }) => {
  const { t } = useTranslation()
  const shopTheLookLabel = t('Shoppe das Outfit')
  const imageUrl = getImageUrl(outfit.hotspotImageHash, {
    trim: false,
    width: 600,
    height: 800,
    bg: 'f2f2f2',
  })

  if (!imageUrl) {
    return null
  }
  return (
    <ElementTrackingContextProvider>
      <PromotionImpression<HTMLDivElement>
        type={TrackingPageTypeWithId.ODP}
        id={outfit.id}
        name={outfit.title}
        url={outfit.url}
        source={TrackingPromotionCreative.OutfitReco}
        useIdForDestination
      >
        {({ ref, onClickCapture }) => (
          <PromotionLink onClickCapture={onClickCapture} to={outfit.url}>
            <div ref={ref} data-testid="PremiumLooksTile">
              <PremiumGapColumn>
                <OutfitTile>
                  <Gradient />
                  <ImageBox src={imageUrl} />
                </OutfitTile>
                <TitleWrapper>{shopTheLookLabel}</TitleWrapper>
              </PremiumGapColumn>
            </div>
          </PromotionLink>
        )}
      </PromotionImpression>
    </ElementTrackingContextProvider>
  )
}
